// Open-position card — card-view counterpart of PositionTracker's Open
// Positions table row: side + venue badges, market header, qty/avg/now/cost
// stats, and a P&L / cash-out / if-win footer with a close-edit action.

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { VenueChip } from '../atoms/VenueChip';
import { GameDetailLink } from '../atoms/GameDetailLink';
import {
  CardShell,
  CardHeader,
  CardBody,
  CardFooter,
  StatLine,
  SideBadge,
  BetTeamTitle,
} from './cardKit';
import { fmtCents, fmtQty, fmtUsd, fmtUsdSigned, fmtVol } from './cardFormat';
import { nowTone, type PositionCardVM } from './positionCardModel';
import { DivergenceBadge } from './DivergenceBadge';
import { UnabatedDivergenceBadge, UPOddsValue, type UPOdds } from './upOdds';
import type { DivergenceInfo } from '@/lib/divergence/polyKalshiDivergence';

function PriceWithOdds({
  cents,
  odds,
}: {
  cents: number | null;
  odds: string | null;
}) {
  return (
    <>
      {fmtCents(cents)}
      {odds && <span className="ml-1.5 text-[11px]">{odds}</span>}
    </>
  );
}

function CompactStat({
  label,
  value,
  valueClass = 'text-foreground',
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground truncate text-[10px] leading-none">{label}</div>
      <div className={`mt-0.5 truncate font-mono text-[11px] font-semibold leading-tight ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}

export function CompactPositionItem({
  vm,
  onEdit,
  onOpen,
  detailHref,
  hidePnl,
  attribution,
  divergence,
  divergenceFlash = false,
}: {
  vm: PositionCardVM;
  onEdit: () => void;
  onOpen?: () => void;
  /** GameDetail page URL — renders the game-detail icon when present.
   *  Row click opens the order panel; the icon is the explicit path to
   *  the detail page (and signals one exists). */
  detailHref?: string | null;
  /** Mask P&L dollars (displaySettings.showPnl off — the page default). */
  hidePnl?: boolean;
  /** Optional owning-trader tag shown in the top row (org view only). */
  attribution?: ReactNode;
  /** Cross-venue divergence for this market, or null (badge self-gates). */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
}) {
  const pnlClass =
    hidePnl || vm.pnlDollars == null
      ? 'text-muted-foreground'
      : vm.pnlDollars >= 0
        ? 'text-positive'
        : 'text-negative';

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onOpen || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      title={vm.ticker}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className={`bg-card px-2.5 py-2 transition-colors hover:bg-muted/20 ${
        onOpen ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70' : ''
      } ${divergenceFlash ? 'animate-divergence-flash' : ''}`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <SideBadge side={vm.side} team={vm.team} />
        <VenueChip venue={vm.venue} />
        <div className="min-w-0 flex-1">
          <div className="text-foreground flex items-center gap-1 text-xs font-semibold leading-tight">
            <span className="min-w-0 truncate">
              <BetTeamTitle title={vm.title} team={vm.team} />
            </span>
            {detailHref && <GameDetailLink href={detailHref} className="-my-1" />}
          </div>
          {vm.subtitle && (
            <div className="text-muted-foreground truncate font-mono text-[10px] leading-tight">
              {vm.subtitle}
            </div>
          )}
        </div>
        <DivergenceBadge info={divergence} />
        {attribution && <div className="shrink-0">{attribution}</div>}
        <div
          className={`shrink-0 font-mono text-xs font-semibold ${pnlClass}`}
          title={hidePnl && vm.pnlDollars != null ? 'P&L hidden — click the eye on the Unrealized P&L tile to reveal' : undefined}
        >
          {vm.pnlDollars == null ? '—' : hidePnl ? '•••' : fmtUsdSigned(vm.pnlDollars)}
        </div>
        {vm.editable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            title="Close / edit position"
            className="text-muted-foreground shrink-0 rounded p-1 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>

      <div className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-1 sm:grid-cols-6">
        <CompactStat
          label="qty"
          value={
            <>
              {fmtQty(vm.qty)}
              {vm.pendingSellQty > 0 && (
                <span
                  className="ml-1 text-amber-400"
                  title={`${vm.pendingSellQty} contract(s) on open sell order(s)`}
                >
                  ({fmtQty(vm.pendingSellQty)})
                </span>
              )}
            </>
          }
        />
        <CompactStat
          label="avg"
          value={<PriceWithOdds cents={vm.avgCents} odds={vm.avgOdds} />}
          valueClass="text-muted-foreground"
        />
        <CompactStat
          label="now"
          value={<PriceWithOdds cents={vm.nowCents} odds={vm.nowOdds} />}
          valueClass={nowTone(vm.nowCents)}
        />
        <CompactStat label="cost" value={fmtUsd(vm.costDollars)} />
        <CompactStat
          label="cash"
          value={fmtUsd(vm.cashOutDollars)}
          valueClass="text-foreground"
        />
        <CompactStat
          label="if win"
          value={fmtUsdSigned(vm.ifWinDollars)}
          valueClass={vm.ifWinDollars == null || vm.ifWinDollars >= 0 ? 'text-positive' : 'text-negative'}
        />
      </div>
    </div>
  );
}

export function PositionCard({
  vm,
  onEdit,
  onOpen,
  detailHref,
  hidePnl,
  mini,
  attribution,
  upOdds,
  divergence,
  divergenceFlash = false,
}: {
  vm: PositionCardVM;
  onEdit: () => void;
  onOpen?: () => void;
  /** GameDetail page URL — renders the game-detail icon when present.
   *  Card click opens the order panel; the icon is the explicit path to
   *  the detail page (and signals one exists). */
  detailHref?: string | null;
  /** Mask P&L dollars (displaySettings.showPnl off — the page default). */
  hidePnl?: boolean;
  /** Dense grid variant: drop the low-priority stat lines (start, U/P,
   *  fees, footer row 2) so ~12 cards fit across a wide monitor. */
  mini?: boolean;
  /** Unabated consensus / Polymarket mid for this market, side-native —
   *  computed by PositionTracker's per-second sweep (table parity). */
  upOdds?: UPOdds | null;
  /** Optional header-right slot — the org view passes the owning trader so
   *  attribution rides along in card/mini mode (positions page passes none). */
  attribution?: ReactNode;
  /** Cross-venue divergence for this market, or null (badge self-gates). */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
}) {
  const pnlClass =
    hidePnl || vm.pnlDollars == null
      ? 'text-muted-foreground'
      : vm.pnlDollars >= 0
        ? 'text-positive'
        : 'text-negative';
  const showFooterRow2 = vm.ifWinDollars != null || vm.editable;

  return (
    <CardShell
      title={vm.ticker}
      className={divergenceFlash ? 'animate-divergence-flash' : ''}
      ariaLabel={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
    >
      <CardHeader
        leftBadges={
          <>
            <SideBadge side={vm.side} team={vm.team} />
            <VenueChip venue={vm.venue} />
          </>
        }
        rightBadges={
          <>
            {/* Alert cluster — Poly (violet) and Unabated (cyan) divergence
                pills pop here on a >= 2¢ gap; both self-gate. Keep this slot
                badges-only: the game-detail link rides the title line so a
                firing badge never crowds it. */}
            <DivergenceBadge info={divergence} />
            <UnabatedDivergenceBadge odds={upOdds} nowCents={vm.nowCents} />
            {attribution}
          </>
        }
        title={
          <span className="flex items-center gap-1">
            <span className="min-w-0 truncate">
              <BetTeamTitle title={vm.title} team={vm.team} />
            </span>
            {detailHref && <GameDetailLink href={detailHref} className="-my-1" />}
          </span>
        }
        subtitle={vm.subtitle}
      />

      <CardBody>
        <StatLine
          label="qty"
          value={
            <>
              {fmtQty(vm.qty)}
              {vm.pendingSellQty > 0 && (
                <span
                  className="ml-1 text-amber-400"
                  title={`${vm.pendingSellQty} contract(s) on open sell order(s)`}
                >
                  ({fmtQty(vm.pendingSellQty)})
                </span>
              )}
            </>
          }
        />
        <StatLine
          label="avg"
          value={<PriceWithOdds cents={vm.avgCents} odds={vm.avgOdds} />}
        />
        {!mini && <StatLine label="start" value={vm.eventStartLabel || '—'} />}
        <StatLine
          label="now"
          value={<PriceWithOdds cents={vm.nowCents} odds={vm.nowOdds} />}
          valueClass={nowTone(vm.nowCents)}
        />
        {/* U/P — Unabated consensus / Polymarket mid, side-native (parity
            with the table's U/P column; replaced the 1m Δ price chip).
            Always present — unresolved sources render as "—/—" so the stat
            grid doesn't reflow as data arrives. */}
        {!mini && <StatLine label="U/P" value={<UPOddsValue odds={upOdds} />} />}
        <StatLine label="cost" value={fmtUsd(vm.costDollars)} />
        {!mini && vm.volume != null && vm.volume > 0 && (
          <StatLine label="vol" value={fmtVol(vm.volume)} />
        )}
        {!mini && (
          <StatLine
            label="fees"
            value={vm.feesCents != null ? fmtUsd(vm.feesCents / 100) : '—'}
          />
        )}
      </CardBody>

      <CardFooter>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground text-xs">
            P&L{' '}
            <span
              className={`font-mono font-semibold ${pnlClass}`}
              title={hidePnl && vm.pnlDollars != null ? 'P&L hidden — click the eye on the Unrealized P&L tile to reveal' : undefined}
            >
              {vm.pnlDollars == null ? '—' : hidePnl ? '•••' : fmtUsdSigned(vm.pnlDollars)}
            </span>
          </span>
          <span className="text-muted-foreground text-xs">
            cash out{' '}
            <span className="text-foreground font-mono font-semibold">
              {fmtUsd(vm.cashOutDollars)}
            </span>
          </span>
          {/* Mini drops footer row 2, which is the edit button's normal
              home — a position you can't close from the dense grid is a
              trap, so the pencil joins row 1 here. */}
          {mini && vm.editable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Close / edit position"
              className="text-muted-foreground shrink-0 rounded p-0.5 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
            >
              <Pencil size={11} />
            </button>
          )}
        </div>
        {!mini && showFooterRow2 && (
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-[11px]">
              {vm.ifWinDollars != null && (
                <>
                  if win{' '}
                  <span className="text-foreground">
                    {fmtUsdSigned(vm.ifWinDollars)}
                  </span>
                  {vm.grossPayoutDollars != null && (
                    <span
                      className="ml-1"
                      title="Gross settlement payout before netting entry cost"
                    >
                      pays {fmtUsd(vm.grossPayoutDollars)}
                    </span>
                  )}
                  {vm.impliedOdds && (
                    <span className="ml-1">({vm.impliedOdds})</span>
                  )}
                </>
              )}
            </span>
            {vm.editable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                title="Close / edit position"
                className="text-muted-foreground inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors hover:bg-blue-500/10 hover:text-blue-400"
              >
                <Pencil size={11} />
                edit
              </button>
            )}
          </div>
        )}
      </CardFooter>
    </CardShell>
  );
}
